import { useState } from 'react'
import { useDispatch } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { ButtonProps } from '@mui/material'
import { LoadingButton } from '@mui/lab'
import { createErrorNotification, createSuccessNotification } from '../redux/slices/notifications'
import { Valve } from '../types'
import { updateValve } from '../services/valve'

export interface ValveDetachOutRuuviTagButtonProps extends Omit<
  ButtonProps,
  'children' | 'onClick'
> {
  valve: Valve
  buttonText?: string
  beforeSubmit?: () => void
  afterSubmit?: (valve: Valve) => void
}

export default function ValveDetachOutRuuviTagButton({
  valve,
  buttonText,
  beforeSubmit,
  afterSubmit,
  ...props
}: ValveDetachOutRuuviTagButtonProps) {
  /**
   * The translation function.
   */
  const { t } = useTranslation('valve')

  /**
   * The dispatch function.
   */
  const dispatch = useDispatch()

  /**
   * The submitting state.
   */
  const [isSubmitting, setSubmitting] = useState<boolean>(false)

  /**
   * Handle the "submit" action.
   */
  async function handleSubmit() {
    try {
      if (valve?.outRuuviTagId) {
        setSubmitting(true)
        beforeSubmit?.()

        const updatedValve = await updateValve(valve.name, { outRuuviTag: null })

        dispatch(createSuccessNotification(t('detachOutRuuviTagSuccess')))
        afterSubmit?.(updatedValve)
      }
    } catch (_) {
      dispatch(createErrorNotification(t('detachOutRuuviTagError')))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <>
      {valve?.outRuuviTagId && (
        <LoadingButton
          variant="contained"
          color="warning"
          loading={isSubmitting}
          {...props}
          onClick={handleSubmit}
        >
          {buttonText ?? t('detachOutRuuviTag')}
        </LoadingButton>
      )}
    </>
  )
}
